// The one place this app talks to the server.
//
// THE DONE CRITERION FOR THIS FILE IS A GREP: `fetch(` appears here and
// nowhere else under `apps/web/src`. There were seven call sites before
// 2026-09-17 — two in screens — and each had written its own answer to the
// same four questions: what does `credentials` need to be, what happens when
// the body is not JSON, what happens when the shape is wrong, and what is the
// difference between a refusal and a dead network. Seven answers, four of
// them different, two of them wrong.
//
// THE THREE OUTCOMES, AND WHY NOT TWO. A call ends one of three ways and a
// caller genuinely has to tell them apart:
//
//   ok          — the server answered and the body is the shape we asked for.
//   refused     — the server answered and said no, in a sentence written for
//                 the person, with a code a screen can branch on.
//   unreachable — we have no answer. The request threw, or what came back was
//                 not this API, or it was and we cannot read it.
//
// Collapsing `refused` into `unreachable` turns "That league is full — ask
// Dave to open a seat" into "Something went wrong", which names nobody who
// can fix it. Collapsing the other way is worse: it reports a network blip as
// the server's considered judgement.
//
// WHY `credentials` IS WRITTEN OUT. It is the default for a same-origin
// request, so it changes nothing today. The cookie IS the session, and a
// refactor that moved these calls to another origin would break sign-in
// silently rather than loudly. It costs one line to make that visible.
//
// VALIDATE AT THE BOUNDARY (CLAUDE.md). Every response is parsed with the
// schema from `@fsp/domain` that the server validated on the way out. Both
// ends of the wire now check the same shape against the same declaration, so
// a drift is caught twice and rendered never.

import { z } from 'zod';
import {
  ApiError,
  UNLOCKED_HEADER,
  BOOKMARK_HEADER,
  type ApiErrorDetails,
  type ErrorCode,
  type Failure,
} from '@fsp/domain';
import { connection } from './connection';
import { reportError } from './errorReports';
import type { KeptCopy } from './kept';
import { askMeNow } from './meFeed';

/**
 * What a call answered.
 *
 * `unreachable` carries a `Failure` rather than being a bare marker, because
 * the three ways to have no answer need three different sentences: the site
 * is not responding, the site answered something that is not this API, and
 * this build is too old to read what it said.
 */
export type Reply<T> =
  | { status: 'ok'; data: T; asOf: string }
  | {
      status: 'refused';
      code: ErrorCode;
      message: string;
      details: ApiErrorDetails | null;
      /** As below, for a refusal that says only "not now" (`NOT_NOW` in `kept.ts`). */
      kept?: KeptCopy;
    }
  | {
      status: 'unreachable';
      failure: Failure;
      /**
       * What this device kept from the last answer to the same read, when the
       * shared reads found one (`read.ts`, `kept.ts`, B-557). Never set by
       * this file: a copy is not an answer, and only a reader draws it.
       */
      kept?: KeptCopy;
    };

/** The envelope, as little of it as we have to trust before parsing properly. */
const Envelope = z.object({
  data: z.unknown().optional(),
  error: z.unknown().optional(),
});

type Unreachable = Extract<Reply<never>, { status: 'unreachable' }>;

function unreachable(message: string, code: Failure['code'] = 'unreachable'): Unreachable {
  return { status: 'unreachable', failure: { code, message } };
}

/**
 * How long a request may go unanswered before we stop waiting and say so.
 *
 * NO REQUEST WAITS FOREVER (B-133). Until 2026-09-24 nothing here had a limit,
 * and a server that accepted the connection and never answered — two local
 * wrangler servers wedged after two days of reloads — gave Ken an empty hub
 * with nothing on it saying why. Every screen's "the wait is bounded, because
 * a failure ends it" was true only for a request that failed; one that hangs
 * never fails. Fifteen seconds is far past any answer this API gives on a
 * working day and short enough that a person is still looking at the screen.
 */
export const NO_ANSWER_MS = 15_000;

/**
 * The sentence for a request that got no answer in time.
 *
 * A READ AND A WRITE ARE DIFFERENT HERE, which is why this is its own code
 * rather than `unreachable`. A read that timed out changed nothing, so trying
 * again is safe. A write that timed out may have landed — the server can have
 * done the work and simply not replied — so "Nothing was saved", which a
 * caller may say for `unreachable`, would be a guess. It says it cannot tell.
 *
 * AND IT DOES NOT SAY "RELOAD". The first cut did, and a reload is the one
 * move that loses work: the priorities editor holds its unsaved list in
 * memory, and the league wizard holds its answers and the Idempotency-Key
 * that makes pressing Create again safe. Every write this app makes is safe
 * to repeat — a whole-document PUT, or a POST with its key, or a join that
 * answers 200 for a seat already held — so "try again" is the true advice.
 */
function noAnswer(writing: boolean): Reply<never> {
  return unreachable(
    writing
      ? 'The site did not answer in time, so we cannot tell whether that went through. Wait a moment and try again.'
      : 'The site is not answering. It may be busy or restarting — try again in a moment.',
    'timeout',
  );
}

/** What a request with no network at all says (B-240). */
const OFFLINE = "You're offline. Connect and try again.";

/**
 * The sentence for a request that never completed.
 *
 * WITH NO NETWORK AT ALL, SAY THAT (B-240): the installed app's cold start in
 * a tunnel lands here before it knows who is signed in, and "check your
 * connection" is a guess when the browser already knows. Exported for the
 * reads that draw what the device kept before their request has failed
 * (`read.ts`), which say the same thing it will.
 */
export function noConnection(): Unreachable {
  return unreachable(
    typeof navigator !== 'undefined' && navigator.onLine === false
      ? OFFLINE
      : 'Could not reach the site. Check your connection.',
  );
}

/** Settles never, and rejects when `signal` aborts — raced against anything that might hang. */
function whenAborted(signal: AbortSignal): Promise<never> {
  return new Promise((_, reject) => {
    if (signal.aborted) reject(signal.reason);
    else signal.addEventListener('abort', () => reject(signal.reason), { once: true });
  });
}

/**
 * A parse failure, classified.
 *
 * API.md's compatibility rule is additive-only: fields are added, never
 * removed or renamed, and a breaking change is a new path. The one thing that
 * rule still allows to break an installed client is a new member in a closed
 * set — a league status, a chore kind — so that is the case worth telling
 * apart. When EVERY issue is an unrecognized option, the likeliest reading is
 * a newer server and an older build, and "reload" is something a person can
 * act on. Anything else is drift and says so.
 *
 * The plan for this phase called for an `openEnum` that mapped an
 * unrecognized value to `'unknown'` at parse. This is that idea's honest
 * half. The other half would have had to either widen every view's TypeScript
 * type with a member the server can never send — so that every `switch` in
 * every screen grew a branch for a value that does not exist — or let the
 * SERVER'S own output validation rewrite a bad value into `'unknown'` and
 * serve it, which is precisely the silent fallback the house rules forbid.
 * Naming the failure costs neither.
 */
function driftFrom(error: z.ZodError, asked?: string): Reply<never> {
  const reply = classified(error);
  // REPORTED (scenario O7): an answer this build cannot read is either a
  // server that broke its contract or a build older than a value it was
  // sent, and either way the person saw a sentence instead of the screen.
  if (asked !== undefined && reply.status === 'unreachable') {
    const where = error.issues
      .slice(0, 3)
      .map((i) => `${i.path.join('.') || '(body)'}: ${i.message}`)
      .join('; ');
    void reportError('drift', `${reply.failure.code} at ${where}`, { api: asked });
  }
  return reply;
}

function classified(error: z.ZodError): Reply<never> {
  const optionsOnly =
    error.issues.length > 0 &&
    error.issues.every(
      (i) =>
        // A `z.enum` miss.
        (i.code === 'invalid_value' && (i.values?.length ?? 0) > 1) ||
        // A `z.discriminatedUnion` with no matching member. Zod 4 reports it
        // as one `invalid_union` AT THE DISCRIMINATOR'S PATH with an empty
        // `errors` list — a plain `z.union` miss has the member errors in it,
        // and that one really is drift. Seven served views are unions on
        // `kind`, and a new chore kind was the very example this
        // classification was written for (review 2026-09-18, M3).
        (i.code === 'invalid_union' && (i.errors?.length ?? 0) === 0),
    );
  if (optionsOnly) {
    return unreachable(
      'This page is out of date. Reload to get the newest version.',
      'app_outdated',
    );
  }
  const where = error.issues[0]?.path.join('.') || 'the body';
  return unreachable(
    `The site answered in a shape this page does not understand (${where}).`,
    'drift',
  );
}

/**
 * THE LAST BOOKMARK A WRITE WAS ANSWERED WITH (API.md §1.6, B-266), sent on
 * every request after it, so a read that comes from the database's nearest
 * copy is at least as fresh as this person's own last change. Kept for the
 * page's life: a copy catches up in seconds, so a reload has nothing to keep.
 */
let bookmark: string | null = null;

async function call<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  init: RequestInit,
): Promise<Reply<z.output<S>>> {
  // OUR OWN SIGNAL, fed by two things: the caller's abort, forwarded, and the
  // no-answer timer. Both the request and the body are raced against it, so a
  // server that sends headers and then stalls is caught as surely as one that
  // sends nothing.
  const writing = init.method !== undefined && init.method !== 'GET';
  // Its place in line, so a request sent into a dead minute that times out
  // after the connection is back is not taken for news (`connection.ts`).
  const ticket = connection.begin();
  const controller = new AbortController();
  const caller = init.signal ?? null;
  const forward = () => controller.abort(caller?.reason);
  if (caller?.aborted) forward();
  else caller?.addEventListener('abort', forward, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, NO_ANSWER_MS);
  const stopped = whenAborted(controller.signal);
  // Nobody may be listening when it rejects — the request usually wins.
  stopped.catch(() => undefined);

  try {
    let response: Response;
    try {
      response = await Promise.race([
        fetch(path, {
          credentials: 'same-origin',
          ...init,
          headers:
            bookmark === null
              ? init.headers
              : {
                  ...(init.headers as Record<string, string> | undefined),
                  [BOOKMARK_HEADER]: bookmark,
                },
          signal: controller.signal,
        }),
        stopped,
      ]);
    } catch {
      // A CALL ITS CALLER CALLED OFF says nothing about the connection
      // (`connection.ts`); one that got no answer does.
      if (caller?.aborted !== true) connection.noAnswer(ticket);
      if (timedOut) return noAnswer(writing);
      // The request never completed. This is also what an abort looks like, so
      // a caller that aborts checks its own signal before rendering anything —
      // see `useRead` in `read.ts`, which is where that discipline lives.
      //
      return noConnection();
    }

    // THE ACTION EARNED SOMETHING (A4): ask the pulse now, once this answer
    // has been read, so the moment arrives with the action rather than on the
    // bell's next tick. The unlocks themselves come from its `unlocked`.
    if (response.headers.get(UNLOCKED_HEADER) !== null) setTimeout(askMeNow, 0);
    // THE NEWEST, not the last to arrive: two writes in flight can answer out
    // of order, and a bookmark is fixed-width hex, so the larger is the later.
    const handed = response.headers.get(BOOKMARK_HEADER);
    if (handed !== null && (bookmark === null || handed > bookmark)) bookmark = handed;

    let body: unknown;
    try {
      body = await Promise.race([response.json(), stopped]);
    } catch {
      if (caller?.aborted !== true) connection.noAnswer(ticket);
      if (timedOut) return noAnswer(writing);
      // A non-JSON answer from /api means something is in front of us that
      // should not be: the SPA shell on a 404, a proxy's error page, a captive
      // portal. Not a refusal — nothing here refused anything.
      return unreachable('Could not reach the site. Check your connection.');
    }

    const reply = settle(body, schema, `${init.method ?? 'GET'} ${path.split('?')[0] ?? path}`);
    // THIS API ANSWERED, whatever it said: a refusal and a shape this build
    // cannot read are answers about the request, not about the connection.
    // JSON that is not this API's envelope is something in front of it.
    if (reply.status === 'unreachable' && reply.failure.code === 'unreachable')
      connection.noAnswer(ticket);
    else connection.answered(ticket);
    return reply;
  } finally {
    clearTimeout(timer);
    caller?.removeEventListener('abort', forward);
  }
}

/**
 * An answer read as one schema, read again as another.
 *
 * For the shared reads (`readStore.ts`): one request answers every reader of
 * a path, each with its own schema, so the request is made as `z.unknown()`
 * and each reader checks the body against its own declaration here. The
 * outcome is the one `settle` would have reached had that reader asked alone.
 */
export function reparse<S extends z.ZodTypeAny>(
  reply: Reply<unknown>,
  schema: S,
  /** The request, for a report when it does not read: `GET /api/hub`. */
  asked?: string,
): Reply<z.output<S>> {
  if (reply.status !== 'ok') return reply;
  const parsed = schema.safeParse(reply.data);
  if (!parsed.success) return driftFrom(parsed.error, asked);
  return { status: 'ok', data: parsed.data, asOf: reply.asOf };
}

/** An answer that arrived, read against the envelope and then the schema. */
function settle<S extends z.ZodTypeAny>(
  body: unknown,
  schema: S,
  asked: string,
): Reply<z.output<S>> {
  const envelope = Envelope.safeParse(body);
  if (!envelope.success) return unreachable('The site answered something that is not our API.');

  if (envelope.data.error !== undefined) {
    const parsed = ApiError.safeParse(envelope.data.error);
    if (!parsed.success) return driftFrom(parsed.error, asked);
    return {
      status: 'refused',
      code: parsed.data.code,
      message: parsed.data.message,
      details: parsed.data.details ?? null,
    };
  }

  // A 2xx with neither key is not a success we can use, and treating it as
  // one puts `undefined` through the schema and reports the wrong failure.
  if (envelope.data.data === undefined) {
    return unreachable('The site answered something that is not our API.');
  }

  const parsed = schema.safeParse(envelope.data.data);
  if (!parsed.success) return driftFrom(parsed.error, asked);

  // When the value carries its own timestamp it is the truer one — `asOf` is
  // when the value was true, not when it arrived (ICD-U01 §4).
  const carried = (envelope.data.data as { updatedAt?: unknown; asOf?: unknown }) ?? {};
  const own = typeof carried.asOf === 'string' ? carried.asOf : carried.updatedAt;
  return {
    status: 'ok',
    data: parsed.data,
    // THE ARRIVAL INSTANT IS THE ONE THING THE WIRE CANNOT CARRY. When the
    // payload has no timestamp of its own, `asOf` is when this answer reached
    // the client, and there is no port to ask: the clock lives on the server
    // and asking it would mean a second round trip to describe the first.
    // eslint-disable-next-line no-restricted-syntax
    asOf: typeof own === 'string' ? own : new Date().toISOString(),
  };
}

export interface CallOptions {
  signal?: AbortSignal;
  /** Extra request headers — `Idempotency-Key` is the one this app sends. */
  headers?: Record<string, string>;
  /**
   * Go past the browser cache for this one read.
   *
   * ONE CALLER, AND IT IS THE ONE THAT MUST NOT BE FOOLED. `/api/version` is
   * `max-age=300` because it is a constant per deployment, which is right for
   * every consumer except the About screen — whose whole job is to say whether
   * the API is answering *now*. Without this it reported a healthy API for five
   * minutes after one had stopped answering, which was measured rather than
   * imagined (2026-09-21). A screen that verifies a deploy must not read a
   * cached answer to the question it exists to ask.
   */
  cache?: RequestCache;
}

/** Read. `path` is relative to `/api`, with its own query string if it needs one. */
export function get<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  options: CallOptions = {},
): Promise<Reply<z.output<S>>> {
  return call(`/api${path}`, schema, {
    method: 'GET',
    signal: options.signal,
    headers: options.headers,
    ...(options.cache === undefined ? {} : { cache: options.cache }),
  });
}

/**
 * Write.
 *
 * `body` is omitted rather than sent as `null` when there is nothing to say,
 * so the request carries no `content-type` and the server's `jsonBody` is not
 * asked to parse an empty string.
 */
export function send<S extends z.ZodTypeAny>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  schema: S,
  options: CallOptions = {},
): Promise<Reply<z.output<S>>> {
  const json = body !== undefined;
  return call(`/api${path}`, schema, {
    method,
    signal: options.signal,
    headers: json ? { 'content-type': 'application/json', ...options.headers } : options.headers,
    body: json ? JSON.stringify(body) : undefined,
  }).finally(() => {
    // Whatever it answered, it may have changed something a held read says,
    // and the caller's next line may be to ask again (`readStore.ts`).
    for (const listener of [...written]) listener();
  });
}

const written = new Set<() => void>();

/**
 * Be told when any write has answered — before its caller hears the answer.
 *
 * The shared reads' one way of knowing a held answer may be out of date
 * (`readStore.ts`). Every write in the app comes through `send`, which is the
 * point of this file, so this sees all of them.
 */
export function whenWritten(listener: () => void): () => void {
  written.add(listener);
  return () => written.delete(listener);
}

/**
 * The sentence to show for a reply that is not `ok`.
 *
 * A refusal's message was written by the server FOR THE PERSON — "That league
 * is full. Ask Dave to open a seat." — and is shown as-is. Screens that
 * substitute their own wording here lose the only part of the answer that
 * named somebody who can fix it.
 */
export function messageOf(reply: Reply<unknown>, fallback: string, unreachable?: string): string {
  if (reply.status === 'refused') return reply.message;
  if (reply.status === 'unreachable') {
    // A caller that knows what did NOT happen says so: "Could not reach the
    // site. Nothing was saved." tells somebody whether to press it again,
    // where the generic sentence leaves them guessing. Only for a dead
    // network — drift and an out-of-date build have their own precise
    // sentences and a reassurance about saving would be beside the point,
    // and a `timeout` must never get it: a write nobody answered may have
    // landed, and its own sentence says so. And never with no network at
    // all: the request never left, so a caller's "we cannot tell whether
    // that was sent" is untrue, and "You're offline" is the whole story.
    return unreachable !== undefined &&
      reply.failure.code === 'unreachable' &&
      reply.failure.message !== OFFLINE
      ? unreachable
      : reply.failure.message;
  }
  return fallback;
}

/** A reply that is not `ok`, as the failure a `Snapshot` carries. */
export function failureFrom(reply: Reply<unknown>): Failure {
  if (reply.status === 'refused') return { code: reply.code, message: reply.message };
  if (reply.status === 'unreachable') return reply.failure;
  throw new Error('failureFrom called on a successful reply');
}
